import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Save, Clock, MapPin, Monitor, AlertCircle, Lock } from 'lucide-react';
import TabTip from '../ui/TabTip';
import { ENCRYPTION_STATEMENT } from '../security/EncryptedBadge';

const SecuritySection = ({
  securitySettings, setSecuritySettings, onSubmit, isLoading,
  loginHistory, loadingHistory
}) => {
  return (
    <div className="space-y-6">
      <div
        data-testid="account-security-encryption-statement"
        className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2"
      >
        <Lock className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" />
        <p className="text-xs text-emerald-900 leading-relaxed">{ENCRYPTION_STATEMENT}</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>Security Settings</span><TabTip tip="security_settings" showIcon={true} />
          </CardTitle>
          <CardDescription>Manage your account security preferences</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Two-Factor Authentication is managed in the Security section near the top of this page.
              </p>
              <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <input type="checkbox" checked={securitySettings.login_alerts} onChange={(e) => setSecuritySettings({ ...securitySettings, login_alerts: e.target.checked })} className="mt-1 w-4 h-4 rounded border-slate-300" />
                <div className="flex-1">
                  <span className="font-medium text-slate-900">Login Alerts</span>
                  <div className="text-sm text-slate-500 mt-1">Get email notifications for new logins to your account</div>
                </div>
              </label>
            </div>
            <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
              <Save className="w-4 h-4 mr-2" />
              {isLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="w-5 h-5" />
            <span>Recent Login Activity</span>
          </CardTitle>
          <CardDescription>Monitor recent access to your account</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory && (
            <div className="text-center py-8 text-slate-500">Loading activity...</div>
          )}
          {!loadingHistory && loginHistory.length === 0 && (
            <div className="text-center py-8 text-slate-500">No login history available</div>
          )}
          {!loadingHistory && loginHistory.length > 0 && (
            <div className="space-y-3">
              {loginHistory.map((login, index) => (
                <div key={`action-${index}`} className="flex items-start justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-start space-x-3 flex-1">
                    <Monitor className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-slate-900 text-sm">{login.device_type || 'Unknown Device'}</span>
                        {login.success ? (
                          <Badge variant="success" className="text-xs bg-green-100 text-green-700 border-green-200">Success</Badge>
                        ) : (
                          <Badge variant="destructive" className="text-xs">Failed</Badge>
                        )}
                        {index === 0 && <Badge variant="outline" className="text-xs">Current</Badge>}
                      </div>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center space-x-1"><MapPin className="w-3 h-3" /><span>{login.location || 'Unknown location'}</span></span>
                        <span>&bull;</span>
                        <span>{new Date(login.timestamp).toLocaleString()}</span>
                      </div>
                      {login.ip_address && <div className="text-xs text-slate-400 mt-1">IP: {login.ip_address}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {loginHistory.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700">
                  If you notice any suspicious activity, please change your password immediately and contact support.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySection;
